import { toast } from "react-toastify";
import Department from "models/department";
import DepartmentMapping, { ExternalSystem } from "models/department-mapping";
import DepartmentKPI from "models/department-kpi";
import FiscalYear from "models/fiscal-year";
import Branch from "models/branch";
import EmployeeDepartmentPermission from "models/employee-department-permission";
import { DepartmentLevel } from "models/department-level";
import Employee from "models/employee";
import DepartmentFields from "Components/organisms/forms/department-form/types";
import DepartmentKPIFields from "Components/organisms/forms/department-kpi-form/types";

export const getDepartments = async (): Promise<{ departments: Department[]; isGlobalAdmin: boolean } | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return { departments: data.data as Department[], isGlobalAdmin: !!data.isGlobalAdmin };
    } catch (err: any) {
        toast.error(err.message ?? "Error getting Departments");
        return null;
    }
}

export const getDepartment = async (departmentId: string): Promise<Department | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as Department;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting Department");
        return null;
    }
}

export const getDepartmentFormFields = async (): Promise<{
    departmentLevels: DepartmentLevel[];
    departments: Department[];
    employees: Employee[];
} | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/form-fields`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting department form fields");
        return null;
    }
}

export const submitCreateDepartmentForm = async (vals: DepartmentFields): Promise<Department | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(vals),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Department created.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error creating Department");
        return null;
    }
}

export const submitEditDepartmentForm = async (
    departmentId: string,
    vals: DepartmentFields
): Promise<Department | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PATCH',
            body: JSON.stringify(vals),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Department updated.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error editing Department");
        return null;
    }
}

export const deleteDepartmentRequest = async (departmentId: string): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'DELETE',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Department deleted.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error deleting Department");
        return false;
    }
}

export const getDepartmentPermissions = async (departmentId: string): Promise<EmployeeDepartmentPermission[] | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/permissions`;

        const res = await fetch(url, { credentials: 'include', method: 'GET' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as EmployeeDepartmentPermission[];
    } catch (err: any) {
        toast.error(err.message ?? "Error getting department permissions");
        return null;
    }
}

export const grantDepartmentPermission = async (
    departmentId: string,
    employeeId: string,
    permissionTypeName: string
): Promise<EmployeeDepartmentPermission | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/permissions`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ employeeId, permissionTypeName }),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Permission granted.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error granting permission");
        return null;
    }
}

export const revokeDepartmentPermission = async (
    departmentId: string,
    employeeId: string
): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/permissions/${employeeId}`;

        const res = await fetch(url, { credentials: 'include', method: 'DELETE' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Permission revoked.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error revoking permission");
        return false;
    }
}

export const getDepartmentMappings = async (departmentId: string): Promise<DepartmentMapping[] | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/mappings`;

        const res = await fetch(url, { credentials: 'include', method: 'GET' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as DepartmentMapping[];
    } catch (err: any) {
        toast.error(err.message ?? "Error getting department mappings");
        return null;
    }
}

export const addDepartmentMappingRequest = async (
    departmentId: string,
    id: string,
    sourceTypeId: string
): Promise<DepartmentMapping | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/mappings`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ id, sourceTypeId }),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Mapping added.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error adding mapping");
        return null;
    }
}

export const updateDepartmentMappingRequest = async (
    departmentId: string,
    mappingId: string,
    id: string
): Promise<DepartmentMapping | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/mappings/${mappingId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PATCH',
            body: JSON.stringify({ id }),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Mapping updated.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error updating mapping");
        return null;
    }
}

export const removeDepartmentMappingRequest = async (
    departmentId: string,
    mappingId: string
): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/mappings/${mappingId}`;

        const res = await fetch(url, { credentials: 'include', method: 'DELETE' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Mapping removed.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error removing mapping");
        return false;
    }
}

export const getDepartmentKPIs = async (departmentId: string): Promise<DepartmentKPI[] | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/kpis`;

        const res = await fetch(url, { credentials: 'include', method: 'GET' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as DepartmentKPI[];
    } catch (err: any) {
        toast.error(err.message ?? "Error getting department KPIs");
        return null;
    }
}

// GET /departments/kpis: KPIs of every department the user can read (Department KPIs module)
export const getAllDepartmentKPIs = async (): Promise<DepartmentKPI[] | null> => {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/departments/kpis`, { credentials: 'include', method: 'GET' });
        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }
        const data = await res.json();
        return data.data as DepartmentKPI[];
    } catch (err: any) {
        toast.error(err.message ?? "Error getting department KPIs");
        return null;
    }
}

export const getDepartmentKPIFormFields = async (): Promise<{ fiscalYears: FiscalYear[], branches: Branch[] } | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/kpi-form-fields`;

        const res = await fetch(url, { credentials: 'include', method: 'GET' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting department KPI form fields");
        return null;
    }
}

export const submitCreateDepartmentKPIForm = async (
    departmentId: string,
    vals: DepartmentKPIFields
): Promise<DepartmentKPI | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/kpis`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify(vals),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Department KPIs created.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error creating department KPIs");
        return null;
    }
}

export const submitEditDepartmentKPIForm = async (
    departmentId: string,
    branchId: string,
    fiscalYear: number,
    vals: DepartmentKPIFields
): Promise<DepartmentKPI | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/kpis/${branchId}/${fiscalYear}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PATCH',
            body: JSON.stringify(vals),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Department KPIs updated.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error updating department KPIs");
        return null;
    }
}

export const deleteDepartmentKPIRequest = async (
    departmentId: string,
    branchId: string,
    fiscalYear: number
): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/${departmentId}/kpis/${branchId}/${fiscalYear}`;

        const res = await fetch(url, { credentials: 'include', method: 'DELETE' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Department KPIs deleted.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error deleting department KPIs");
        return false;
    }
}

export const getMappingSources = async (): Promise<ExternalSystem[] | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/departments/mapping-sources`;

        const res = await fetch(url, { credentials: 'include', method: 'GET' });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as ExternalSystem[];
    } catch (err: any) {
        toast.error(err.message ?? "Error getting mapping sources");
        return null;
    }
}
